import {AfterViewInit,Component,ElementRef,Injector,OnDestroy,OnInit,ViewChild,computed,signal} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { SignaturePadService } from '../../topaz/signature-pad.service';
import { MainComponent } from 'src/modules/base/components/main-component/main.component';
import { ActivatedRoute } from '@angular/router';
import { DialogRef } from 'src/modules/base/components/dialog/dialog-ref';
@Component({
  selector: 'app-signature-topaz-pad',
  standalone: false,
  templateUrl: './signature-topaz-pad.component.html',
  styleUrl: './signature-topaz-pad.component.css',
})
export class SignatureTopazPadComponent extends MainComponent implements OnInit {
  @ViewChild('padCanvas') private readonly padCanvasRef!: ElementRef<HTMLCanvasElement>;

  /** The bill row this popup was opened for, assigned by DynamicPopUpComponent after creation. */
  data: any;

  readonly sdkAvailable = signal(false);
  readonly connected = signal(false);
  readonly signatureImage = signal<string | null>(null);
  readonly statusMessage = signal('Not connected');
  readonly saving = signal(false);

  /**
   * Angular's default URL sanitizer rejects data URIs that don't exactly
   * match its base64 pattern (no whitespace allowed). The image comes from
   * locally installed Topaz hardware, not untrusted user input, so it's
   * safe to explicitly trust it rather than fight the sanitizer's regex.
   */
  readonly safeSignatureImage = computed<SafeUrl | null>(() => {
    const image = this.signatureImage();
    return image ? this.sanitizer.bypassSecurityTrustUrl(image) : null;
  });

  constructor(
    private readonly signaturePad: SignaturePadService,
    private readonly sanitizer: DomSanitizer,
    private readonly dialog: DialogRef,
    public injector: Injector,
    public route: ActivatedRoute,
  ) {
    super(injector, route);
  }

  ngOnInit(): void {
    console.log("abc");
  }
  async ngAfterViewInit(): Promise<void> {
    this.statusMessage.set('Loading signature pad SDK…');
    const sdkAvailable = await this.signaturePad.loadSdk();
    this.sdkAvailable.set(sdkAvailable);
    if (!sdkAvailable) {
      this.statusMessage.set(
        'SigPlusExtLite extension/wrapper not detected. Install SigPlusExtLite.exe plus the ' +
          'browser extension (see src/assets/topaz/README.md), then reload this page.'
      );
      return;
    }

    const ok = await this.signaturePad.connect(this.padCanvasRef.nativeElement);
    this.connected.set(ok);
    this.statusMessage.set(ok ? 'Connected to signature pad' : 'Could not connect to pad');
  }

  async ngOnDestroy(): Promise<void> {
    await this.signaturePad.disconnect();
  }

  async onClear(): Promise<void> {
    await this.signaturePad.clear();
    this.signatureImage.set(null);
  }

  async onSave(): Promise<void> {
    const image = await this.signaturePad.captureBase64Image();
    if (!image) {
      this.statusMessage.set('No signature captured yet — sign on the pad first.');
      return;
    }
    this.signatureImage.set(image);

    if (!this.data?.id) {
      this.alerts.error('Missing bill reference for this signature.');
      return;
    }

    this.saving.set(true);
    this.loader.showSpinner();
    try {
      const { signature_document_id } = await this.putSignatureInS3(this.data, image);
      if (!signature_document_id) {
        this.alerts.error('Failed to upload signature.');
        return;
      }
      await this.updateResult({ id: this.data.id, patient_signature_id: signature_document_id });
      this.alerts.success('Signature saved successfully.');
      this.dialog.close();
    } catch (err) {
      console.error('Error saving patient signature', err);
      this.alerts.error('Failed to save signature.');
    } finally {
      this.saving.set(false);
      this.loader.hideSpinner();
    }
  }

  async updateResult(result: any): Promise<void> {
    const datasource = {
      application: 'billing',
      resource: 'billing_bill_no',
      query_id: 'UPLOAD_PATIENT_SIGNATURE',
    };
    await firstValueFrom(
      this.baseService.action(result, datasource.query_id, datasource.application, datasource.resource)
    );
  }

  async putSignatureInS3(data: any, signature: string): Promise<{ signature_document_id: string }> {
    const { file, fileName } = this.base64ToBlob(data, signature);
    const formData = new FormData();
    formData.append('file', file, fileName);
    formData.append('fileName', fileName);
    formData.append('action', 'add');
    const dmsRes: any = await firstValueFrom(this.baseService.upload(formData, 'dms', 'mediagallery', signature));

    if (dmsRes?.errCode === 0 && dmsRes?.resource?.length > 0) {
      const { documentId } = dmsRes;
      return { signature_document_id: documentId };
    }
    return { signature_document_id: '' };
  }

  base64ToBlob(data: any, signature: string): { file: Blob, fileName: string } {
    const arr = signature.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    const fileName = `patient_signature_${data.id}.png`;
    const file = new File([u8arr], fileName, { type: mime });
    return { file, fileName };
  }
}
